import { Navbar } from "../Navigation/Navbar";
import {Button, Dropdown, Image, Select, Space, Card, Badge, Typography} from "antd";
import { audioMessage as AntdMessage } from "../Common/Notifications/audioMessage";
import React, {useEffect, useRef, useState} from "react";

import "../../Styles/styles-Chatbot.css";
import {CloseOutlined, DownOutlined} from "@ant-design/icons";
import { Skeleton } from "antd";
import { useSelector } from "react-redux";
import { TrainingAPI } from "../../api/TrainingAPI";
import {FaRegThumbsDown, FaThumbsDown} from "react-icons/fa";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import ReactMarkdown from "react-markdown";
import { HelpButton } from "../Common/HelpButton";
import BlurredChatbot from "./blurred-chatbot.png";
import { PremiumFeatureDisabled } from "../Common/PremiumFeatureDisabled";
import {useAuth0} from "@auth0/auth0-react";
import spinner from '../../Images/chatbot-spinner.svg';
import { AdminNotice } from "../Settings/AdminNotice";

let trainingAPI = new TrainingAPI();

const SAMPLE_MESSAGE_DATA = [
  {
    id: 1,
    message: "Hi there! Ask me a question!",
    type: "text",
    sender: "agent",
    metadata: {}
  }
]

const FALLBACK_ERROR_MESSAGE = "Sorry, I didn't understand that. Please try again.";

const isNotDefaultIntroMessage = (message) => message.id !== 1;


/////////////////
// V1 Components
/////////////////

function MessageListV1({ messages, isTyping }) {
  const {
    info: { team_colors: teamColors },
  } = useSelector(({ team }) => team);

  const isAIMessage = (message) => message.sender === "agent";

  const TextMessage = ({ message }) => {
    const textColor = getMessageColor(teamColors, message);

    return (
      <div
        className={`chatbot-message-bubble-text ${
          isAIMessage(message)
            ? "chatbot-message-bubble-text-ai"
            : "chatbot-message-bubble-text-user"
        }`}
      >
        <ReactMarkdown
          components={{
            code({ inline, children, ...props }) {
              return inline ? (
                <code color={textColor} {...props}>
                  {children}
                </code>
              ) : (
                <pre style={{ color: textColor, background: "none", padding: "0", margin: "0" }} {...props}>
                  {children}
                </pre>
              );
            },
          }}
        >
          {message?.message || FALLBACK_ERROR_MESSAGE}
        </ReactMarkdown>
      </div>
    )
  }

  const TableMessage = ({ message }) => {
    const renderTable = (data) => {
      if (!data || data.length === 0) return null;
      const [headers, ...rows] = data;

      return (
        <table className="chat-interface-table">
          <thead className="chat-interface-table-head">
          <tr className="chat-interface-table-header-tr">
            {headers.map((header, index) =>
              <th
                key={index}
                className="chat-interface-th"
                style={{
                  backgroundColor: teamColors.secondary,
                  color: teamColors.font_colors.length > 1 ? teamColors.font_colors[1] : "white",
                  padding: "5px"
                }}
              >
                {header}
              </th>
            )}
          </tr>
          </thead>
          <tbody className="chat-interface-table-body">
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="chat-interface-table-body-tr"
            >
              {row.map((cell, cellIndex) =>
                <td
                  key={cellIndex}
                  className="chat-interface-td"
                  style={{
                    borderColor: teamColors.font_colors.length > 1 ? teamColors.font_colors[1] : "white",
                    borderColorOpacity: 0.8,
                  }}
                >
                  {cell}
                </td>)}
            </tr>
          ))}
          </tbody>
        </table>
      );
    };

    return (
      <>
        {renderTable(message.result)}
      </>
    )
  }

  const ChartMessage = ({ message }) => {
    const handleTooltipFunction = (functionString) => {
      if (!functionString) return null;
      const bodyMatch = functionString.match(/function\s*\(\)\s*\{([\s\S]*)\}/);

      if (bodyMatch && bodyMatch[1]) {
        const functionBody = bodyMatch[1].trim();
        return new Function(functionBody);
      }
      return null;
    }

    const formatChartOptions = (chartData) => {
      return {
        ...chartData,
        ...(
          chartData?.tooltip ? {
            tooltip: {
              ...chartData.tooltip,
              // formatter: new Function(chartData?.tooltip?.formatter)
              formatter: handleTooltipFunction(chartData?.tooltip?.formatter)
            }
          } : {})
      }
    }

    return (
      <>
        <HighchartsReact highcharts={Highcharts} options={formatChartOptions(message?.chart || {})} />
      </>
    )
  }

  const DummyMessage = ({ message }) => {
    return (
      <>
        <p>Message of type {message.type}</p>
      </>
    )
  }

  // Function to decide which message type component to render
  const messageComponent = (message) => {
    switch (message.type) {
      case 'text':
        return <TextMessage message={message} />;
      case 'table':
        return <TableMessage message={message} />;
      case 'chart':
        return <ChartMessage message={message} />;
      // Add more cases as needed for different message types
      default:
        return <DummyMessage message={message} />;  // Default to TextMessage if no type is matched
    }
  };

  const MessageReview = ({ message, messages }) => {
    /*
    Where beta users can review messages = mark bad AI messages + add notes/corrections
     */
    const [toggleReview, setToggleReview] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');

    const onThumbDownClick = () => {
      setToggleReview(!toggleReview);
    }

    const submitMessageForReview = async () => {
      try {
        // Send message to backend for review
        const res = trainingAPI.reviewAIMessage(message.message, message.metadata, messages, reviewNotes);
        if (res) {
          await AntdMessage.success("AI Message Reviewed");
          setToggleReview(false);

        } else {
          console.error("Failed to submit message for review");
        }
      } catch (e) {
        console.error("Error submitting message for review: ", e);
      }
    }

    return (
      <div className="chatbot-message-review">
        {
          toggleReview ? (
            <FaThumbsDown
              className="chatbot-message-review-icon"
              onClick={onThumbDownClick}
            />
          ) : (
            <FaRegThumbsDown
              className="chatbot-message-review-icon"
              onClick={onThumbDownClick}
            />
          )
        }

        {
          toggleReview ? (
            <>
              <textarea
                className="chatbot-message-review-textarea"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="(optional) How should the AI respond?"
              />
              <button
                className="chatbot-message-review-submit"
                onClick={submitMessageForReview}
              >
                Review AI Message
              </button>
            </>
          ) : <></>
        }
      </div>
    )
  }

  const getMessageColor = (teamColors, message) => {
    if (!Object.keys(teamColors).length) return 'gray';

    return teamColors.font_colors[isAIMessage(message) ? 0 : 1];
  }

  return (
    <div className="chatbot-messages-container">
      {messages.map(message => (
        <div
          className="chatbot-message-bubble-outer"
          style={{
            alignSelf: isAIMessage(message) ? "flex-start" : "flex-end",
          }}
        >
          <div
            key={message.id}
            className="chatbot-message-bubble"
            style={{
              backgroundColor: isAIMessage(message) ? teamColors.primary : teamColors.secondary,
              color: getMessageColor(teamColors, message),
            }}
          >
            {messageComponent(message)}
          </div>
          {
            isAIMessage(message) && isNotDefaultIntroMessage(message)
              ? <MessageReview message={message} messages={messages} />
              : <></>
          }
        </div>
      ))}

      {
        isTyping ? (
          <div
            className="chatbot-message-bubble"
            style={{
              backgroundColor: teamColors.primary,
              color: teamColors.font_colors.length ? teamColors.font_colors[0] : "white",
              alignSelf: "flex-start"
            }}
          >
            {
              Array(3).fill(1).map((_, index) => (
                <Skeleton.Button
                  active
                  size='small'
                  shape='circle'
                  className="chatbot-typing-indicator-bubble"
                />
              ))
            }
          </div>
        ) : <></>
      }
    </div>
  );
}

/////////////////
// V0 Components
/////////////////


class TableDataInterface {
  constructor(apiResponse) {
    this.response = apiResponse;
  }

  getTableData() {
    const columnNames = this.response?.outbound_content?.result?.column_names || [];
    const data = this.response?.outbound_content?.result?.data || [];
    return [
      columnNames,
      ...data
    ]
  }

}

const FollowUpMessageButtons = ({ messages, isLoading, onClick }) => {
  const handleClick = (message) => {
    onClick(message?.question);
  }

  if (isLoading === undefined || messages === undefined || messages.length === 0) {
    return (
      <></>
    )
  }

  return (
    <div className="chat-followup-messages-container">
      {
        isLoading ? (
            <div className="chat-followup-loading">
              Loading...
            </div>
        ) : (
            messages?.map((message, _) => (
                <div
                    key={message?.key}
                    className="chat-followup-message-button"
                    onClick={() => handleClick(message)}
                >
                  <p className="chat-followup-message-text">{message?.key}</p>
                </div>
            ))
        )
      }
    </div>
  )
}

function MessageList({ messages, isTyping, streamingUuid, sendMessage }) {
  const { getAccessTokenSilently } = useAuth0();
  const eventSourceRef = useRef(null); // Track EventSource instance
  const {
    info: { team_colors: teamColors },
  } = useSelector(({ team }) => team);
  const [intermediateMessages, setIntermediateMessages] = useState([]);
  const [followUpMessages, setFollowUpMessages] = useState([]);
  const [followUpLoading, setFollowUpLoading] = useState(false);

  useEffect(() => {
    if (!streamingUuid) return;

    let isMounted = true;

    async function setupEventSource() {
      try {
        const token = await getAccessTokenSilently();

        if (!isMounted) return;

        const eventSource = new EventSource(`${trainingAPI.url}/rte/${streamingUuid}/?token=${token}`);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
          const data = event?.data;
          const parsedData = JSON.parse(data);

          if (isFollowUpMessage(parsedData)) {
            if (parsedData?.status === "in-progress") {
              setFollowUpLoading(true);
            } else if (parsedData?.status === "done") {
              const messageString = JSON.stringify(parsedData?.message?.content);
              const messages = JSON.parse(messageString);
              setFollowUpLoading(false);
              setFollowUpMessages(JSON.parse(messages).follow_up_questions);
            }
            return;
          }

          setIntermediateMessages(prev => [...prev, parsedData])
          if (parsedData?.status === "done") {
            setIntermediateMessages([])
          }
        };

        eventSource.onerror = (err) => {
          eventSource.close();
        };
      } catch (err) {
        console.error("Failed to setup EventSource:", err);
      }
    }

    setupEventSource();

    return () => {
      isMounted = false;
      if (eventSourceRef?.current) {
        eventSourceRef?.current?.close();
      }
    };
  }, [streamingUuid, getAccessTokenSilently]);

  //////////////////////
  // Follow Up Messages
  //////////////////////

  const isFollowUpMessage = (message) => {
    return message?.event_type === "chatbot_followup"
  }

  const isAIMessage = (message) => message.sender === "agent";

  const TextMessage = ({ message }) => {
    const textColor = getMessageColor(teamColors, message);

    return (
      <div
        className={`chatbot-message-bubble-text ${
          isAIMessage(message)
            ? "chatbot-message-bubble-text-ai"
            : "chatbot-message-bubble-text-user"
        }`}
      >
        <ReactMarkdown
          components={{
            code({ inline, children, ...props }) {
              return inline ? (
                <code color={textColor} {...props}>
                  {children}
                </code>
              ) : (
                <pre style={{ color: textColor, background: "none", padding: "0" }} {...props}>
                {children}
              </pre>
              );
            },
          }}
        >
          {message?.message || FALLBACK_ERROR_MESSAGE}
        </ReactMarkdown>
      </div>
    )
  }

  const TableMessage = ({ message }) => {
    function snakeToTitleCase(str) {
      return str
        .split(/[_ ]+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    const renderTable = (data) => {
      if (!data || data.length === 0) return null;
      const [headers, ...rows] = data;

      return (
        <table className="chat-interface-table">
          <thead className="chat-interface-table-head">
          <tr className="chat-interface-table-header-tr">
            {headers.map((header, index) =>
              <th
                key={index}
                className="chat-interface-th"
                style={{
                  backgroundColor: teamColors.secondary,
                  color: teamColors.font_colors.length > 1 ? teamColors.font_colors[1] : "white",
                  padding: "10px"
                }}
              >
                {snakeToTitleCase(header)}
              </th>
            )}
          </tr>
          </thead>
          <tbody className="chat-interface-table-body">
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="chat-interface-table-body-tr"
            >
              {row.map((cell, cellIndex) =>
                <td
                  key={cellIndex}
                  className="chat-interface-td"
                  style={{
                    borderColor: teamColors.font_colors.length > 1 ? teamColors.font_colors[1] : "white",
                    borderColorOpacity: 0.8,
                    textAlign: (cellIndex === 0 ? 'left' : 'right')
                  }}
                >
                  {cell}
                </td>)}
            </tr>
          ))}
          </tbody>
        </table>
      );
    };

    return (
      <>
        {renderTable(message.message)}
      </>
    )
  }

  const ChartMessage = ({ message }) => {
    const handleTooltipFunction = (functionString) => {
      if (!functionString) return null;
      const bodyMatch = functionString.match(/function\s*\(\)\s*\{([\s\S]*)\}/);

      if (bodyMatch && bodyMatch[1]) {
        const functionBody = bodyMatch[1].trim();
        return new Function(functionBody);
      }
      return null;
    }

    const formatChartOptions = (chartData) => {
      return {
        ...chartData,
        ...(
          chartData?.tooltip ? {
            tooltip: {
              ...chartData.tooltip,
              // formatter: new Function(chartData?.tooltip?.formatter)
              formatter: handleTooltipFunction(chartData?.tooltip?.formatter)
            }
          } : {})
      }
    }

    return (
      <>
        <HighchartsReact highcharts={Highcharts} options={formatChartOptions(message?.message || {})} />
      </>
    )
  }

  const DummyMessage = ({ message }) => {
    return (
      <>
        <p>Message of type {message.type}</p>
      </>
    )
  }

  // Function to decide which message type component to render
  const messageComponent = (message) => {
    switch (message.type) {
      case 'text':
        return <TextMessage message={message} />;
      case 'table':
        return <TableMessage message={message} />;
      case 'chart':
        return <ChartMessage message={message} />;
      // Add more cases as needed for different message types
      default:
        return <DummyMessage message={message} />;  // Default to TextMessage if no type is matched
    }
  };

  const MessageReview = ({ message, messages }) => {
    /*
    Where beta users can review messages = mark bad AI messages + add notes/corrections
     */
    const [toggleReview, setToggleReview] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');

    const onThumbDownClick = () => {
      setToggleReview(!toggleReview);
    }

    const submitMessageForReview = async () => {
      try {
        // Send message to backend for review
        const res = trainingAPI.reviewAIMessage(message.message, message.metadata, messages, reviewNotes);
        if (res) {
          await AntdMessage.success("AI Message Reviewed");
          setToggleReview(false);

        } else {
          console.error("Failed to submit message for review");
        }
      } catch (e) {
        console.error("Error submitting message for review: ", e);
      }
    }

    return (
      <div className="chatbot-message-review">
        {
          toggleReview ? (
            <FaThumbsDown
              className="chatbot-message-review-icon"
              onClick={onThumbDownClick}
            />
          ) : (
            <FaRegThumbsDown
              className="chatbot-message-review-icon"
              onClick={onThumbDownClick}
            />
          )
        }

        {
          toggleReview ? (
            <>
              <textarea
                className="chatbot-message-review-textarea"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="(optional) How should the AI respond?"
              />
              <button
                className="chatbot-message-review-submit"
                onClick={submitMessageForReview}
              >
                Review AI Message
              </button>
            </>
          ) : <></>
        }
      </div>
    )
  }

  const getMessageColor = (teamColors, message) => {
    if (!Object.keys(teamColors).length) return 'gray';

    return teamColors.font_colors[isAIMessage(message) ? 0 : 1];
  }

  const intermediateMessageToRender = () => {
    // render last intermediate message
    if (intermediateMessages.length === 0) return null;
    return intermediateMessages[intermediateMessages.length - 1];
  }

  const onClickFollowUpMessage = (question) => {
    setFollowUpMessages([]);
    setFollowUpLoading(false);
    sendMessage(question, 'user');
  }

  return (
    <div className="chatbot-messages-container">
      {messages.map(message => (
        <div
          className="chatbot-message-bubble-outer"
          style={{
            alignSelf: isAIMessage(message) ? "flex-start" : "flex-end",
          }}
        >
          <div
            key={message.id}
            className="chatbot-message-bubble"
            style={{
              backgroundColor: isAIMessage(message) ? teamColors.primary : teamColors.secondary,
              color: getMessageColor(teamColors, message),
            }}
          >
            {messageComponent(message)}
          </div>
          {
            isAIMessage(message) && isNotDefaultIntroMessage(message)
              ? <MessageReview message={message} messages={messages} />
              : <></>
          }
        </div>
      ))}
      {/* — intermediate streaming chunks — */}
      {isTyping ? (
          <div
              className="chatbot-message-bubble chatbot-message-bubble-intermediate"
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                alignSelf: "flex-start",
                opacity: 0.7,
                fontStyle: "italic",
                backgroundColor: teamColors.primary,
                color: teamColors.font_colors?.length ? teamColors.font_colors[0] : "white",
                fontSize: '0.8rem'
              }}
          >
            {
              isTyping && (
                <div>
                  <img src={spinner} alt="Loading…" className="chatbot-typing-spinner" />
                </div>
            )}
            <div style={{ marginLeft: 8 }}>
              {intermediateMessageToRender() ? intermediateMessageToRender()?.message?.content : ""}
            </div>
          </div>
      ) : <></>}
      <FollowUpMessageButtons
          messages={followUpMessages}
          isLoading={followUpLoading || isTyping}
          onClick={(question) => {
              onClickFollowUpMessage(question)
          }}
      />

    </div>
  );
}

function ChatHeader() {
  /*
  Contains:
  - exit button
  - chatbot name
   */
  const {
    info: { team_logo: team_logo },
  } = useSelector(({ team }) => team);

  return (
    <div className="chat-interface-header-container">
      <div className="chat-interface-header-icon-container">
        <Image
          id="chat-interface-team-logo"
          src={team_logo}
          width={32}
          preview={false}
        />
      </div>
      <div className="chat-interface-header-title-container">
        <p className="chat-interface-header-title">DataViz AI</p>
        <p className="chat-interface-header-subtitle">Talk to your data...</p>
      </div>
      <div className="chat-interface-header-exit">

      </div>
    </div>
  )
}

function SendMessageForm({ onSendMessage, isTyping }) {
  const [text, setText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSendMessage(text, 'user');
    setText('');
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        type="text"
        value={text}
        className="chatbot-send-message-input"
        onChange={(e) => setText(e.target.value)}
        placeholder="Ask me a question..."
      />
      <button
        type="submit"
        className="chatbot-send-message-button"
        disabled={isTyping}
      >
        Send
      </button>
    </form>
  );
}


const SuggestedQuestions = ({ questions, sendMessage }) => {
  const {
    info: { team_colors: teamColors },
  } = useSelector(({ team }) => team);

  const handleClick = (question) => {
    sendMessage(question, 'user');
  }

  if (!questions || questions.length === 0) {
    return <></>;
  }

  return (
      <div className="chat-suggested-container">
        {
            questions.map((question, index) => {
              return (
                  <div
                      key={index}
                      className="chat-suggested-question"
                      style={{
                        "--hover-bg": teamColors.secondary,      // e.g. "#6658f6"
                        "--hover-color": teamColors.font_colors[0] // e.g. "#ffffff"
                      }}
                      onClick={() => handleClick(question)}
                  >
                    {question}
                  </div>
              )
            })
        }
      </div>
  )
}


export const ChatbotView = () => {
  /*
  This is the embedded UI so far, we also need something similar-ish for a floating widget
   */
  const { user } = useAuth0();
  const [messages, setMessages] = useState(SAMPLE_MESSAGE_DATA);
  const [isTyping, setIsTyping] = useState(false);
  const [chatbotVersion, setChatbotVersion] = useState("0.1");  // Default to V0
  const [conversationId, setConversationId] = useState(null);

  const [streamingUuid, setStreamingUuid] = useState(null);

  const {
    info : {dvai_enabled},
    dvaiSuggestedQuestions,
  } = useSelector(({team}) => team);

  const isStaffEmail = user?.email?.endsWith("gpsdataviz.com");

  const appendNewMessage = (newMessage) => {
    setMessages(prevMessages => {
      return [...prevMessages, newMessage]
    });
  }

  const sendMessageV1 = async (text, sender) => {
    appendNewMessage({
      id: Date.now(),
      message: text,
      type: 'text',
      sender: sender
    });
    setIsTyping(true);

    try {
      const res = await trainingAPI.callChatbotApi({
        inboundMessage: text,
        messages,
        conversationId,
        version: "1.0"
      });

      setConversationId(res.conversation_id)

      const agentMessages = res.messages || [];
      for (const message of agentMessages) {
        appendNewMessage({
          id: Date.now(),
          ...message
        });
      }
    } catch (err) {
      console.error("Error sending API: ", err);
      appendNewMessage({
        id: Date.now(),
        message: FALLBACK_ERROR_MESSAGE,
        type: 'text',
        sender: "agent"
      });
      setIsTyping(false);
      return;
    }

    setIsTyping(false);
  }

  const sendMessage = async (text, sender) => {
    /*
    Todos:
    - some type of loading indicator
    - error handling
      - if failed response from backend
    - handle different message types (tables, charts etc.)
    - sync with backend for Message Object (replace IDs etc.)
    - conversation history / reset conversation
    */

    if (chatbotVersion === "1.0") {
      await sendMessageV1(text, sender);
      return;
    }

    appendNewMessage({
      id: Date.now(),
      message: text,
      type: 'text',
      sender: sender
    });
    setIsTyping(true);

    try {
      const newUuid = crypto.randomUUID();
      setStreamingUuid(newUuid);
      const res = await trainingAPI.callChatbotApi({
        inboundMessage: text,
        messages,
        conversationId,
        streamingUuid: newUuid
      });

      appendNewMessage({
        id: Date.now(),
        message: res.outbound_content?.message,
        metadata: {
          ...res.metadata,
          'inbound_message': res.inbound_message,
          outbound_response: res.outbound_content,
          action: res.action,
        },
        type: 'text',
        sender: "agent"
      });

      if (res.outbound_content?.type === "table") {
        const tableData = new TableDataInterface(res);
        appendNewMessage({
          id: Date.now(),
          message: tableData.getTableData(),
          type: 'table',
          sender: "agent"
        })
      }

      if (res.outbound_content?.type === "chart") {
        appendNewMessage({
          id: Date.now(),
          message: res.outbound_content?.chart,
          type: 'chart',
          sender: "agent"
        })
      }

      setConversationId(res.conversation_id)
    } catch (err) {
      console.error("Error sending API: ", err);
      appendNewMessage({
        id: Date.now(),
        message: FALLBACK_ERROR_MESSAGE,
        type: 'text',
        sender: "agent"
      });
      setIsTyping(false);
      return;
    }

    setIsTyping(false)

  };

  const versionOptions = [
    { key: "0.1", label: "V0" },
    { key: "1.0", label: "V1 (Beta)" }
  ]

  const versionLocked = messages.length > 1;

  if (!dvai_enabled && !isStaffEmail) {
    return (
      <PremiumFeatureDisabled
        bg_image={BlurredChatbot}
        feature_name={"DataViz AI"}
        info_text={<>
        This is an elite feature that will allow you to talk to your data in seconds. <br/> <br/> 
        If you are interested in adding this to your platform, please contact your sales rep!</>}
      />
    )
  }

  const renderSuggestions = () => {
    return messages.length <= 1
  }

  return (
    <>
      <Navbar />
      <HelpButton page="Dataviz AI" />

      {(!dvai_enabled && isStaffEmail) && (
        <div style={{ margin: '16px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
          <AdminNotice />
        </div>
      )}

      <div className="chatbot-view-content">
        <div className="chatbot-container">
          <ChatHeader />
          {/*/!*Dropdown to select version*!/*/}
          {/*<Select*/}
          {/*  defaultValue={chatbotVersion}*/}
          {/*  onChange={setChatbotVersion}*/}
          {/*  disabled={versionLocked}*/}
          {/*>*/}
          {/*  {*/}
          {/*    versionOptions.map(option => (*/}
          {/*      <Select.Option*/}
          {/*        key={option.key}*/}
          {/*        value={option.key}*/}
          {/*      >*/}
          {/*        {option.label}*/}
          {/*      </Select.Option>*/}
          {/*    ))*/}
          {/*  }*/}
          {/*</Select>*/}
          {
            chatbotVersion === "1.0" ? (
              <MessageListV1
                messages={messages}
                isTyping={isTyping}
              />
            ) : (
              <MessageList
                messages={messages}
                isTyping={isTyping}
                streamingUuid={streamingUuid}
                sendMessage={sendMessage}
              />
            )
          }
          {
            renderSuggestions() ? (
                <SuggestedQuestions
                    questions={dvaiSuggestedQuestions}
                    sendMessage={sendMessage}
                />
            ) : <></>
          }
          <SendMessageForm
              onSendMessage={sendMessage}
              isTyping={isTyping}
          />
        </div>
      </div>
    </>
  );
}